import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { TableFormData } from '../types';

interface TableFormProps {
  initialData?: TableFormData;
  existingTableNumbers?: number[];
  onSubmit: (data: TableFormData) => Promise<void>;
  onCancel: () => void;
}

interface FormErrors {
  number?: string;
  capacity?: string;
}

export const TableForm: React.FC<TableFormProps> = ({
  initialData,
  existingTableNumbers = [],
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<TableFormData>({
    number: initialData?.number || 0,
    capacity: initialData?.capacity || 0,
    shape: initialData?.shape || 'square',
    position: initialData?.position || { x: 100, y: 100 }
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.number || formData.number <= 0) {
      newErrors.number = 'Número de mesa es requerido';
    } else if (existingTableNumbers.includes(formData.number) && formData.number !== initialData?.number) {
      newErrors.number = 'Número de mesa ya existe';
    }

    if (!formData.capacity || formData.capacity <= 0) {
      newErrors.capacity = 'Capacidad debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNumberChange = (value: string) => {
    const number = parseInt(value) || 0;
    setFormData({ ...formData, number });
    if (errors.number) {
      setErrors({ ...errors, number: undefined });
    }
  };

  const handleCapacityChange = (value: string) => {
    const capacity = parseInt(value) || 0;
    setFormData({ ...formData, capacity });
    if (errors.capacity) {
      setErrors({ ...errors, capacity: undefined });
    }
  };

  const handleShapeChange = (value: string) => {
    setFormData({ 
      ...formData, 
      shape: value as TableFormData['shape']
    });
  };

  const shapeOptions = [
    { value: 'square', label: 'Cuadrada' },
    { value: 'round', label: 'Redonda' },
    { value: 'rectangle', label: 'Rectangular' }
  ];

  const isEditing = !!initialData;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Número de Mesa */}
        <div className="space-y-2">
          <Label htmlFor="number">Número de Mesa</Label>
          <Input
            id="number"
            type="number"
            min="1"
            value={formData.number || ''}
            onChange={(e) => handleNumberChange(e.target.value)}
            className={errors.number ? 'border-red-500' : ''}
          />
          {errors.number && (
            <p className="text-sm text-red-500">{errors.number}</p>
          )}
        </div>

        {/* Capacidad */}
        <div className="space-y-2">
          <Label htmlFor="capacity">Capacidad (personas)</Label>
          <Input
            id="capacity"
            type="number"
            min="1"
            max="20"
            value={formData.capacity || ''}
            onChange={(e) => handleCapacityChange(e.target.value)}
            className={errors.capacity ? 'border-red-500' : ''}
          />
          {errors.capacity && (
            <p className="text-sm text-red-500">{errors.capacity}</p>
          )}
        </div>
      </div>

      {/* Forma */}
      <div className="space-y-2">
        <Label htmlFor="shape">Forma de la Mesa</Label>
        <Select value={formData.shape} onValueChange={handleShapeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar forma" />
          </SelectTrigger>
          <SelectContent>
            {shapeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Información de Posición (solo para referencia) */}
      {isEditing && (
        <div className="space-y-2">
          <Label>Posición Actual</Label>
          <p className="text-sm text-muted-foreground">
            X: {formData.position.x}, Y: {formData.position.y}
          </p>
          <p className="text-xs text-muted-foreground">
            La posición se puede ajustar directamente en el mapa
          </p>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-macartur-primary hover:bg-macartur-accent1"
        >
          {isSubmitting 
            ? (isEditing ? 'Actualizando...' : 'Creando...') 
            : (isEditing ? 'Actualizar Mesa' : 'Crear Mesa')
          }
        </Button>
      </div>
    </form>
  );
};